import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Tv, Upload, Save, Trash2, Eye, EyeOff, Play, 
  Settings, Image, Link, Type, Clock, Loader2,
  CheckCircle, AlertCircle, Sparkles, ArrowLeft, RefreshCw,
  Pencil
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSponsors() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [config, setConfig] = useState({
    intro_enabled: false,
    intro_duration: 2.0,
    intro_fade_duration: 0.5,
    outro_enabled: false,
    outro_duration: 2.0,
    outro_fade_duration: 0.5,
    watermark_enabled: false,
    watermark_position: 'bottom-right',
    watermark_opacity: 0.3,
    watermark_scale: 0.1,
    active_sponsor_id: null
  });
  
  // New sponsor form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newSponsor, setNewSponsor] = useState({
    name: '',
    website: '',
    tagline: 'Made more interesting by',
    logo_data: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Edit sponsor
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);

  const fetchSponsors = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSponsors(data.sponsors || []);
      if (data.config) {
        setConfig(data.config);
      }
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      toast.error('Error al cargar sponsors');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
      setNewSponsor(prev => ({ ...prev, logo_data: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleCreateSponsor = async () => {
    if (!newSponsor.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSponsor)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Sponsor creado exitosamente');
        setShowNewForm(false);
        setNewSponsor({ name: '', website: '', tagline: 'Made more interesting by', logo_data: null });
        setLogoPreview(null);
        fetchSponsors();
      } else {
        toast.error(data.detail || 'Error al crear sponsor');
      }
    } catch (error) {
      toast.error('Error al crear sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (sponsorId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/${sponsorId}/set-active`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setConfig(prev => ({ ...prev, active_sponsor_id: sponsorId }));
      } else {
        toast.error(data.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al configurar sponsor activo');
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('¿Estás seguro de eliminar este sponsor?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/${sponsorId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Sponsor eliminado');
        fetchSponsors();
      } else {
        toast.error(data.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al eliminar sponsor');
    }
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor({
      ...sponsor,
      logo_data: null // Will be set if user uploads new logo
    });
    setEditLogoPreview(sponsor.has_logo ? `${API_URL}/api/sponsors/${sponsor.id}/logo` : null);
  };

  const handleEditLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditLogoPreview(e.target.result);
      setEditingSponsor(prev => ({ ...prev, logo_data: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateSponsor = async () => {
    if (!editingSponsor) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/${editingSponsor.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingSponsor.name,
          website: editingSponsor.website,
          tagline: editingSponsor.tagline,
          logo_data: editingSponsor.logo_data
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Sponsor actualizado');
        setEditingSponsor(null);
        setEditLogoPreview(null);
        fetchSponsors();
      } else {
        toast.error(data.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al actualizar sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Configuración guardada');
        setConfig(data.config);
      } else {
        toast.error(data.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableAll = async () => {
    if (!window.confirm('¿Desactivar todo el branding de sponsors en los videos?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/disable-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Branding desactivado');
        fetchSponsors();
      }
    } catch (error) {
      toast.error('Error');
    }
  };

  const activeSponsor = sponsors.find(s => s.id === config.active_sponsor_id) || sponsors.find(s => s.is_default);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-sponsors-page">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2 whitespace-nowrap">
                <Tv className="w-5 h-5 text-primary" />
                Sponsors & Branding
              </h1>
              <p className="text-sm text-muted-foreground">
                Configura intro, outro y marcas de agua
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisableAll}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hidden sm:flex"
            >
              <EyeOff className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Desactivar</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Sparkles className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuevo</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">

      {/* Active Sponsor Preview */}
      {activeSponsor && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-gradient-to-r from-[#BBFF00]/10 to-emerald-500/10 border border-[#BBFF00]/30"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-black/50 flex items-center justify-center overflow-hidden">
              {activeSponsor.has_logo ? (
                <img 
                  src={`${API_URL}/api/sponsors/${activeSponsor.id}/logo`} 
                  alt={activeSponsor.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Tv className="w-8 h-8 text-[#BBFF00]" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#BBFF00]" />
                <span className="text-[#BBFF00] font-medium">Sponsor Activo</span>
              </div>
              <h3 className="text-white font-bold text-lg">{activeSponsor.name}</h3>
              <p className="text-slate-400 text-sm">{activeSponsor.website}</p>
            </div>
            <div className="text-right text-sm text-slate-400">
              <p>Intro: {config.intro_enabled ? `${config.intro_duration}s` : 'Desactivada'}</p>
              <p>Outro: {config.outro_enabled ? `${config.outro_duration}s` : 'Desactivada'}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Configuration Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Intro/Outro Config */}
        <div className="p-5 rounded-xl bg-[#131B2A] border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-[#BBFF00]" />
            Intro & Outro
          </h3>
          
          <div className="space-y-5">
            {/* Intro */}
            <div className="p-4 rounded-lg bg-black/30 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Intro (Inicio del video)</Label>
                <Switch
                  checked={config.intro_enabled}
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, intro_enabled: v }))}
                />
              </div>
              {config.intro_enabled && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">Duración: {config.intro_duration}s</Label>
                  <Slider
                    value={[config.intro_duration]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, intro_duration: v }))}
                    min={1}
                    max={5}
                    step={0.5}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            {/* Outro */}
            <div className="p-4 rounded-lg bg-black/30 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Outro (Final del video)</Label>
                <Switch
                  checked={config.outro_enabled}
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, outro_enabled: v }))}
                />
              </div>
              {config.outro_enabled && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400">Duración: {config.outro_duration}s</Label>
                  <Slider
                    value={[config.outro_duration]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, outro_duration: v }))}
                    min={1}
                    max={5}
                    step={0.5}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            {/* Fade Duration */}
            <div className="p-4 rounded-lg bg-black/30 space-y-2">
              <Label className="text-xs text-slate-400">Fade In/Out: {config.intro_fade_duration}s</Label>
              <Slider
                value={[config.intro_fade_duration]}
                onValueChange={([v]) => setConfig(prev => ({ 
                  ...prev, 
                  intro_fade_duration: v,
                  outro_fade_duration: v 
                }))}
                min={0.2}
                max={1}
                step={0.1}
                className="w-full"
              />
            </div>

            <Button
              onClick={handleSaveConfig}
              disabled={saving}
              className="w-full bg-[#BBFF00] text-black hover:bg-[#a8e600]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Configuración
            </Button>
          </div>
        </div>

        {/* Sponsors List */}
        <div className="p-5 rounded-xl bg-[#131B2A] border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-[#BBFF00]" />
            Sponsors ({sponsors.length})
          </h3>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {sponsors.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Tv className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay sponsors configurados</p>
              </div>
            ) : (
              sponsors.map(sponsor => (
                <div 
                  key={sponsor.id}
                  className={`p-3 rounded-lg border transition-all ${
                    sponsor.id === config.active_sponsor_id
                      ? 'bg-[#BBFF00]/10 border-[#BBFF00]/50'
                      : 'bg-black/30 border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-black/50 flex items-center justify-center overflow-hidden">
                      {sponsor.has_logo ? (
                        <img 
                          src={`${API_URL}/api/sponsors/${sponsor.id}/logo`} 
                          alt={sponsor.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Type className="w-6 h-6 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{sponsor.name}</h4>
                      <p className="text-slate-500 text-xs truncate">{sponsor.website || 'Sin website'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditSponsor(sponsor)}
                        className="text-blue-400 hover:bg-blue-500/10"
                        title="Editar sponsor"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {sponsor.id !== config.active_sponsor_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSetActive(sponsor.id)}
                          className="text-[#BBFF00] hover:bg-[#BBFF00]/10"
                          title="Activar sponsor"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteSponsor(sponsor.id)}
                        className="text-red-400 hover:bg-red-500/10"
                        title="Eliminar sponsor"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {sponsor.tagline && (
                    <p className="text-slate-400 text-xs mt-2 italic">&ldquo;{sponsor.tagline}&rdquo;</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </main>

      {/* New Sponsor Modal */}
      {showNewForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-[#131B2A] rounded-xl border border-white/10 p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4">Nuevo Sponsor</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre *</Label>
                <Input
                  value={newSponsor.name}
                  onChange={(e) => setNewSponsor(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Androide Mexicano"
                  className="mt-1 bg-black/30 border-white/10"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Website</Label>
                <Input
                  value={newSponsor.website}
                  onChange={(e) => setNewSponsor(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="www.androidemexicano.com"
                  className="mt-1 bg-black/30 border-white/10"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Tagline</Label>
                <Input
                  value={newSponsor.tagline}
                  onChange={(e) => setNewSponsor(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="Made more interesting by"
                  className="mt-1 bg-black/30 border-white/10"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Logo</Label>
                <div className="mt-1 flex items-center gap-4">
                  {logoPreview ? (
                    <div className="w-20 h-20 rounded-lg bg-black/50 overflow-hidden">
                      <img src={logoPreview} alt="Preview" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-black/50 flex items-center justify-center">
                      <Image className="w-8 h-8 text-slate-600" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <div className="px-4 py-2 rounded-lg border border-white/20 text-slate-300 hover:bg-white/5 transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Subir Logo
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewForm(false);
                  setLogoPreview(null);
                  setNewSponsor({ name: '', website: '', tagline: 'Made more interesting by', logo_data: null });
                }}
                className="flex-1 border-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateSponsor}
                disabled={saving || !newSponsor.name.trim()}
                className="flex-1 bg-[#BBFF00] text-black hover:bg-[#a8e600]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Crear Sponsor
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Sponsor Modal */}
      {editingSponsor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#131B2A] rounded-xl p-6 w-full max-w-md border border-white/10"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-400" />
              Editar Sponsor
            </h2>
            
            <div className="space-y-4">
              {/* Logo Upload */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg bg-black/50 flex items-center justify-center overflow-hidden border border-white/10">
                  {editLogoPreview ? (
                    <img src={editLogoPreview} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Image className="w-8 h-8 text-slate-500" />
                  )}
                </div>
                <div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditLogoUpload}
                      className="hidden"
                    />
                    <div className="px-4 py-2 rounded-lg border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      {editingSponsor.has_logo ? 'Cambiar Logo' : 'Subir Logo'}
                    </div>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG, WebP</p>
                </div>
              </div>
              
              {/* Name */}
              <div>
                <Label className="text-slate-300">Nombre</Label>
                <Input
                  value={editingSponsor.name}
                  onChange={(e) => setEditingSponsor(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Nombre del sponsor"
                />
              </div>
              
              {/* Website */}
              <div>
                <Label className="text-slate-300">Website</Label>
                <Input
                  value={editingSponsor.website || ''}
                  onChange={(e) => setEditingSponsor(prev => ({ ...prev, website: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="androidemexicano.com"
                />
              </div>
              
              {/* Tagline */}
              <div>
                <Label className="text-slate-300">Tagline / Frase</Label>
                <Input
                  value={editingSponsor.tagline || ''}
                  onChange={(e) => setEditingSponsor(prev => ({ ...prev, tagline: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Patrocinado por"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingSponsor(null);
                  setEditLogoPreview(null);
                }}
                className="flex-1 border-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateSponsor}
                disabled={saving || !editingSponsor.name.trim()}
                className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Cambios
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
